import { CRField } from '@smart-builder/types';
import { isNullOrUndefined, timestampToDate } from '@smart-builder/util';
import { createMainResourceREST, mergeSummaryBodyField } from '../resource';
import {
  CRProjectBody,
  CRProjectBodyAPI,
  CRProjectKey,
  CRProjectSummary,
  ProjectsActions,
  ProjectsState,
  ProjectSummaryAPI,
} from './types';

type AnyProject = CRProjectKey | CRProjectSummary | CRProjectBody | undefined;

function sameKeyProject(a: AnyProject, b: AnyProject): boolean {
  return (a?.projectId || 0) === (b?.projectId || 0);
}

function getEndpointProject(key: AnyProject): string {
  return key ? `/projects/${key.projectId}/` : '/projects/';
}

function timestampFieldToDateField(
  field?: CRField,
  isUTC?: boolean,
): CRField | undefined {
  if (!field) return undefined;

  const { server, ...rest } = field;
  return {
    ...rest,
    server: {
      ...server,
      value: timestampToDate(server.value, isUTC),
    },
  };
}

function dateToUTCTimestamp(date?: Date): string | undefined {
  return isNullOrUndefined(date) ? undefined : '' + date!.getTime() / 1000;
}

function dateToTimestamp(date?: Date): string | undefined {
  return isNullOrUndefined(date)
    ? undefined
    : '' + (date!.getTime() / 1000 - date!.getTimezoneOffset() * 60);
}

function parseProjectSummary(
  projectSummary: ProjectSummaryAPI,
): CRProjectSummary {
  const { dateEntered, dateExpiry, ...rest } = projectSummary;
  return {
    dateEntered: timestampToDate(dateEntered),
    dateExpiry: timestampToDate(dateExpiry),
    ...rest,
  };
}

function formatProjectFieldChange(fieldName: string, value: any): any {
  if (fieldName === 'project/receivedDate') {
    return dateToUTCTimestamp(value);
  } else if (
    fieldName === 'project/dateEntered' ||
    fieldName === 'project/dateOrdered' ||
    fieldName === 'project/dateQuoted' ||
    fieldName === 'project/deliveryDate' ||
    fieldName === 'project/deliveryTime'
  ) {
    return dateToTimestamp(value);
  } else {
    return value;
  }
}

function parseProjectBody(projectBody: CRProjectBodyAPI): CRProjectBody {
  const { projectId, project, orderHistory, ...rest } = projectBody;

  return {
    projectId: projectId || 0, // to handle newly inserted projects
    project: project
      ? {
          ...project,
          dateEntered: timestampFieldToDateField(project.dateEntered)!,
          dateOrdered: timestampFieldToDateField(project.dateOrdered),
          dateQuoted: timestampFieldToDateField(project.dateQuoted),
          deliveryDate: timestampFieldToDateField(project.deliveryDate),
          deliveryTime: timestampFieldToDateField(project.deliveryTime),
          receivedDate: timestampFieldToDateField(project.receivedDate, true),
        }
      : undefined,
    orderHistory: orderHistory?.map(({ sentDate, ...order }) => ({
      ...order,
      sentDate: timestampToDate(sentDate, true)!,
    })),
    ...rest,
  };
}

function mergeProjectBody(lastBody: CRProjectBody, currentBody: CRProjectBody) {
  return currentBody;
}

const projectsStateSelector = (state: any): ProjectsState => state.projects;
const currentProjectSelector = (state: any): CRProjectBody =>
  state.projects?.currentBody;

function combineSummaryBodyProject(
  summary?: CRProjectSummary,
  body?: CRProjectBody,
): CRProjectSummary {
  const doMergeBody = mergeSummaryBodyField(summary, body);
  const doMergeDetail = mergeSummaryBodyField(summary, body?.project);

  const projectId = doMergeBody('projectId')!;

  return {
    projectId,
    chargeable: doMergeBody('chargeable')!,
    chargeableType: (body?.chargeable.chargeableType ??
      summary?.chargeableType)!,
    owner: doMergeBody('owner')!,
    branch: doMergeBody('branch')!,

    customerName: doMergeDetail('customerName'),
    customerReference: doMergeDetail('customerReference'),
    dateEntered:
      body?.project?.dateEntered?.client?.value ?? summary?.dateEntered,
    location: doMergeDetail('location'),
    projectCode: doMergeDetail('projectCode'),
    projectDisplayId: doMergeDetail('projectDisplayId'),
    projectName: doMergeDetail('projectName'),
    quantity: doMergeDetail('quantity'),
    statusId: doMergeDetail('statusId'),
    subTotal: doMergeDetail('subTotal'),
    supplierQuoteNumber: doMergeDetail('supplierQuoteNumber'),
    taxAmount: doMergeDetail('taxAmount'),
    quoteState: doMergeDetail('quoteState'),
    isExternalQuoteExpired: doMergeDetail('isExternalQuoteExpired'),
    hasExternalHardwareStock: doMergeDetail('hasExternalHardwareStock'),
    allowShowHardwareUserExternalOverride: doMergeDetail('allowShowHardwareUserExternalOverride'),

    // match VCL grid, show total price as 0 if its undefined
    totalPrice: isNullOrUndefined(body?.project?.totalPrice?.client.value)
      ? summary?.totalPrice
      : doMergeDetail('totalPrice'),

    dateExpiry: summary?.dateExpiry, // doMergeBody('dateExpiry'),
    description: summary?.description || '', // doMergeBody('description'),
    externalReference: summary?.externalReference, // doMergeBody('externalReference'),
    supplier: summary?.supplier, //doMergeBody('supplier'),
    rowKey: summary?.rowKey || projectId || -1, // (body?.projectId || summary?.rowKey)!, // doMergeBody('rowKey'),
  };
}

const { actions, reducer, watches, rawActions, useListener, useWizard } =
  createMainResourceREST(
    'projects',
    getEndpointProject,
    projectsStateSelector,
    sameKeyProject,
    parseProjectBody,
    parseProjectSummary,
    mergeProjectBody,
    combineSummaryBodyProject,
    formatProjectFieldChange,
    // formatProjectBody,
    // validateProjects,
  );

const projectsActions: ProjectsActions = actions;
export {
  projectsActions,
  useListener as useProjectListener,
  useWizard as useProjectsWizard,
  reducer as projectsReducer,
  watches as projectsWatches,
  projectsStateSelector,
  currentProjectSelector,
  rawActions as projectsRawActions,
};

export * from './types';
